// The Early Access ribbon's artwork: a solid diagonal band across the
// top-left corner of the page with EARLY ACCESS along it, drawn as SVG so
// that only the band is painted and only the band takes the pointer. The
// web app, the extension's popup and the server's own pages
// (crates/server/src/html.rs, which carries a copy of `ribbonArt()` that
// earlyAccess.test.ts compares with this) all draw it from here; its
// colours and type are styles/early-access.css.
//
// The band is the part of a `size` square between the diagonals
// x + y = `inner` and x + y = `size`. Both are whole pixels, so the band's
// edges cross the pixel grid at pixel corners and are drawn the same on
// every row.

export interface RibbonForm {
  /** The side of the corner square the band crosses; the band's outer edge runs from (size, 0) to (0, size). */
  size: number;
  /** Where the band's inner edge meets the window's edges: (inner, 0) and (0, inner). */
  inner: number;
  /** The lettering's size, in px. */
  fontSize: number;
}

/** What the band says, along its middle. */
export const RIBBON_TEXT = 'EARLY ACCESS';

/**
 * The ribbon's two sizes. `full` is a band 24px thick whose legs are 108px;
 * `compact`, 20px thick with legs of 92px, is for a narrow window and the
 * extension's popup. Each is one line of lettering, never less than 10px.
 */
export const RIBBON = {
  full: { size: 108, inner: 74, fontSize: 11 },
  compact: { size: 92, inner: 64, fontSize: 10 },
} as const satisfies Record<string, RibbonForm>;

export type RibbonSize = keyof typeof RIBBON;

/** The band's thickness, in px. */
export const thickness = (form: RibbonForm): number => (form.size - form.inner) / Math.SQRT2;

/**
 * The query parameter that asks the app for its feedback form: a link to
 * the app carrying it (`feedbackUrl`) opens the form for someone signed in.
 */
export const FEEDBACK_PARAM = 'feedback';

/** The address that opens the feedback form of the app at `origin`. */
export const feedbackUrl = (origin: string): string => `${origin}/?${FEEDBACK_PARAM}=1`;

/** What the ribbon is called where it only says so, and where activating it sends feedback. */
export const RIBBON_LABEL = 'Early Access';
export const RIBBON_ACTION_LABEL = 'Early Access — send feedback';

/** The band's corners, clockwise from where its inner edge leaves the top of the window. */
export function bandPoints(form: RibbonForm): [number, number][] {
  return [
    [form.inner, 0],
    [form.size, 0],
    [0, form.size],
    [0, form.inner],
  ];
}

/** Whether the point (x, y), measured from the window's top-left corner, is on the band. */
export function onBand(x: number, y: number, form: RibbonForm): boolean {
  return x >= 0 && y >= 0 && x + y >= form.inner && x + y <= form.size;
}

const points = (ps: [number, number][]) => ps.map((p) => p.join(',')).join(' ');

/** One size's drawing: the band, a 1px line along each of its long edges, its lettering, and the ring shown when it has the focus. */
export function formArt(name: string, form: RibbonForm): string {
  const { size: s, inner: i } = form;
  const at = (i + s) / 4;
  // Each line is the band's outermost pixel: a 1px stroke centred half a pixel in from the edge.
  const half = Math.SQRT1_2;
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const edges = `M${r(i + half)},0L0,${r(i + half)}M${r(s - half)},0L0,${r(s - half)}`;
  // A 2px line whose outside is 2px in from the band's every edge.
  const ring = points([
    [r(i + 3 * Math.SQRT2 - 3), 3],
    [r(s - 3 * Math.SQRT2 - 3), 3],
    [3, r(s - 3 * Math.SQRT2 - 3)],
    [3, r(i + 3 * Math.SQRT2 - 3)],
  ]);
  return (
    `<svg class="ea-art ea-${name}" viewBox="0 0 ${s} ${s}" width="${s}" height="${s}" aria-hidden="true" focusable="false">` +
    `<polygon class="ea-band" points="${points(bandPoints(form))}"/><path class="ea-edge" d="${edges}"/>` +
    `<text class="ea-text" x="${at}" y="${at}" dx=".06em" dy=".36em" text-anchor="middle" font-size="${form.fontSize}" transform="rotate(-45 ${at} ${at})">${RIBBON_TEXT}</text>` +
    `<polygon class="ea-ring" points="${ring}"/></svg>`
  );
}

/** The ribbon's drawing as markup: both sizes, of which early-access.css shows the one that fits. */
export function ribbonArt(): string {
  return formArt('full', RIBBON.full) + formArt('compact', RIBBON.compact);
}
